import { Navigate, useLocation } from 'react-router-dom';
import { useAuthContext } from '@/contexts/AuthContext';
import { Loader2, ShieldX } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAdmin?: boolean;
  requireEditor?: boolean;
}

export function ProtectedRoute({ 
  children, 
  requireAdmin = false,
  requireEditor = false 
}: ProtectedRouteProps) {
  const { user, isLoading, isAdmin, isEditor, signOut } = useAuthContext();
  const location = useLocation();

  // Show loading state
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30">
        <div className="text-center space-y-4">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!user) {
    return <Navigate to="/admin/login" state={{ from: location }} replace />;
  }

  // Check role requirements
  const hasRequiredRole = requireAdmin 
    ? isAdmin 
    : requireEditor 
      ? isEditor 
      : isEditor; // Default: require at least editor role for CMS access

  if (!hasRequiredRole) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30 p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto w-12 h-12 bg-destructive/10 rounded-full flex items-center justify-center mb-4">
              <ShieldX className="w-6 h-6 text-destructive" />
            </div>
            <CardTitle className="font-serif text-2xl">Access Denied</CardTitle>
            <CardDescription>
              {requireAdmin 
                ? "You need administrator privileges to access this area."
                : "You need editor privileges or higher to access the CMS."
              }
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground text-center">
              Contact an administrator to request the appropriate role for your account.
            </p>
            <div className="flex flex-col gap-2">
              <Button asChild variant="outline">
                <Link to="/">Return to Site</Link>
              </Button>
              <Button 
                variant="ghost" 
                onClick={() => signOut()}
                className="text-muted-foreground"
              >
                Sign out
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return <>{children}</>;
}
